import { FireDepartmentsApi, ApiClient } from '../generated/src';
import API_CONFIG from '../config/apiConfig';

/**
 * Service for fetching fire department reference data
 * Handles fetching the list of fire departments available for a user to link via Fire Bridge
 */
class FireDepartmentService {
  constructor() {
    this.apiClient = null;
    this.fireDepartmentsApi = null;
    this.tokenGetter = null;
  }

  /**
   * Initialize the API client with the given base URL
   * @param {string} baseUrl - The Fire Bridge server base URL
   * @param {Function} getTokenFn - Function that returns the current authentication token
   * @throws {Error} If the ApiClient fails to construct with the provided base URL
   */
  initialize(baseUrl, getTokenFn) {
    try {
      this.apiClient = new ApiClient(baseUrl);
      this.apiClient.timeout = API_CONFIG.TIMEOUT;

      if (getTokenFn && typeof getTokenFn === 'function') {
        this.tokenGetter = getTokenFn;
      }

      this.fireDepartmentsApi = new FireDepartmentsApi(this.apiClient);

      console.log(`FireDepartmentService initialized with base URL: ${baseUrl}`);
    } catch (error) {
      console.error('Failed to initialize FireDepartmentService:', error);
      throw error;
    }
  }

  /**
   * Update the authentication token before making API calls.
   * @throws {Error} If the service is not initialized or no token is available.
   */
  updateToken() {
    if (!this.tokenGetter || !this.apiClient) {
      throw new Error('FireDepartmentService not initialized. Call initialize() first.');
    }

    const currentToken = this.tokenGetter();
    if (!currentToken) {
      throw new Error('No authentication token available.');
    }

    this.apiClient.authentications['BearerAuth'].accessToken = currentToken;
  }

  /**
   * Fetch all fire departments
   * @returns {Promise<Array<Object>>} The list of fire departments (FireDepartment[])
   */
  async getAllFireDepartments() {
    if (!this.fireDepartmentsApi) {
      throw new Error('FireDepartmentService not initialized. Call initialize() first.');
    }

    try {
      this.updateToken();
    } catch (error) {
      return Promise.reject(error);
    }

    return new Promise((resolve, reject) => {
      this.fireDepartmentsApi.getAllFireDepartments((error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
    });
  }
}

export const fireDepartmentService = new FireDepartmentService();
export default fireDepartmentService;
